// src/components/workout/WorkoutTemplateManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { ScrollArea } from "../ui/scroll-area";
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Save,
  Dumbbell, 
  LayoutGrid, 
  Calendar,
  Move,
  MoreVertical,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  X,
  Check,
  ChevronsUpDown
} from 'lucide-react';

const WorkoutTemplateManager = ({ 
  userId, 
  userSettings,
  onTemplateDeleted, 
  onTemplateAdded, 
  onTemplateActivated 
}) => {
  const [templates, setTemplates] = useState([]);
  const [customExercises, setCustomExercises] = useState([]);
  const [publicExercises, setPublicExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [selectedWorkoutId, setSelectedWorkoutId] = useState(null);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showWorkoutDialog, setShowWorkoutDialog] = useState(false);
  const [showAddExerciseDialog, setShowAddExerciseDialog] = useState(false);

  // Selected items
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [comboboxValue, setComboboxValue] = useState(""); // Stores the ID of the selected exercise
  const [comboboxSearch, setComboboxSearch] = useState(""); // State for the search input

  // Empty template structure
  const emptyTemplate = {
    name: '',
    description: '',
    goal: 'strength',
    level: 'beginner',
    frequency: '3x per week',
    workouts: {}
  };

  // Empty workout structure
  const emptyWorkout = {
    name: '',
    exercises: []
  };

  // Fetch templates and exercises on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        // Fetch user's custom templates
        const { data: templateData, error: templateError } = await supabase
          .from('workout_templates')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        
        if (templateError) throw templateError;
        setTemplates(templateData || []);
        
        // Fetch user's custom exercises
        const { data: customExercisesData, error: customExercisesError } = await supabase
          .from('custom_exercises')
          .select('*')
          .eq('user_id', userId)
          .order('name');
        
        if (customExercisesError) throw customExercisesError;
        setCustomExercises(customExercisesData || []);
        
        // Fetch public exercises
        const { data: publicExercisesData, error: publicExercisesError } = await supabase
          .from('public_exercises')
          .select('*')
          .order('name');
        
        if (publicExercisesError) throw publicExercisesError;
        setPublicExercises(publicExercisesData || []);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load workout templates',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, toast]);

  const allAvailableExercises = useMemo(() => {
    const combined = [
      ...customExercises.map(ex => ({ ...ex, isPublic: false })),
      ...publicExercises.map(ex => ({ ...ex, isPublic: true }))
    ];
    // Map to the format needed for Combobox/Command (value & label) and sort
    return combined
      .map(ex => ({
        value: ex.id.toString(), // Ensure value is a string ID
        label: ex.name,
        isPublic: ex.isPublic,
        type: ex.exercise_type // Add type for potential display later
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customExercises, publicExercises]);

  useEffect(() => {
    // This effect runs whenever the 'showWorkoutDialog' state value changes.
    console.log("Effect Triggered: showWorkoutDialog state changed to:", showWorkoutDialog);
  }, [showWorkoutDialog]); // Dependency array includes only showWorkoutDialog

  // Handle creating a new template
  const handleCreateTemplate = async () => {
    if (!currentTemplate?.name?.trim()) {
      toast({ title: 'Name Required', description: 'Please provide a name', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      // Insert into Supabase
      const { data, error } = await supabase
        .from('workout_templates')
        .insert({
          name: currentTemplate.name,
          description: currentTemplate.description || '',
          goal: currentTemplate.goal || 'general', // Add defaults if needed
          level: currentTemplate.level || 'beginner',
          frequency: currentTemplate.frequency || 'custom',
          workouts: currentTemplate.workouts || {}, // Ensure workouts is at least {}
          user_id: userId,
          is_active: false // New templates shouldn't be active by default
        })
        .select() // Select the newly created row
        .single(); // Expecting a single row back

      if (error) throw error;
      if (!data) throw new Error("Template created but no data returned.");

      const newTemplateObject = data; // The newly created template with its ID

      // Update this component's local state (add and sort)
      setTemplates(prev =>
          [newTemplateObject, ...prev].sort((a, b) => a.name.localeCompare(b.name))
      );

      // *** Call the onTemplateAdded callback passed from the parent ***
      if (typeof onTemplateAdded === 'function') {
          console.log("TEMPLATE_MANAGER: Calling onTemplateAdded with:", newTemplateObject);
          onTemplateAdded(newTemplateObject); // Pass the full new template object up
      } else {
          console.warn("TEMPLATE_MANAGER: onTemplateAdded prop not provided.");
      }
      // **************************************************************

      // Reset UI
      setShowCreateDialog(false);
      setCurrentTemplate(null);

      toast({
        title: 'Template Created',
        description: `"${newTemplateObject.name}" created successfully.`
      });

    } catch (error) {
      console.error('Error creating template:', error);
      toast({ title: 'Error', description: `Failed to create: ${error.message}`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle updating a template
  const handleUpdateTemplate = async () => {
    if (!currentTemplate?.name?.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please provide a name for the template',
        variant: 'destructive'
      });
      return;
    }
    
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('workout_templates')
        .update({
          name: currentTemplate.name,
          description: currentTemplate.description || '',
          goal: currentTemplate.goal,
          level: currentTemplate.level,
          frequency: currentTemplate.frequency,
          workouts: currentTemplate.workouts || {},
          updated_at: new Date().toISOString()
        })
        .eq('id', currentTemplate.id)
        .eq('user_id', userId)
        .select();
      
      if (error) throw error;
      
      setTemplates(prev => 
        prev.map(t => t.id === currentTemplate.id ? data[0] : t)
      );
      
      setShowEditDialog(false);
      setCurrentTemplate(null);
      
      toast({
        title: 'Template Updated',
        description: `${data[0].name} has been updated successfully`
      });
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: `Failed to update template: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle deleting a template
  const handleDeleteTemplate = async () => {
    // Check if currentTemplate and its id exist
    if (!currentTemplate?.id) {
      toast({ title: 'Error', description: 'No template selected for deletion.', variant: 'destructive' });
      return;
    }

    const templateIdToDelete = currentTemplate.id; // Store ID before potential state reset
    const templateNameToDelete = currentTemplate.name; // Store name for toast

    setSubmitting(true);
    try {
      // Delete from Supabase
      const { error } = await supabase
        .from('workout_templates')
        .delete()
        .eq('id', templateIdToDelete) // Use the stored ID
        .eq('user_id', userId);

      if (error) throw error;

      // Update this component's local template list state
      setTemplates(prev => prev.filter(t => t.id !== templateIdToDelete));

      // *** VVVV THIS IS THE NEW PART VVVV ***
      // Call the callback function passed from the parent (WorkoutPage)
      if (typeof onTemplateDeleted === 'function') {
        console.log(`TEMPLATE_MANAGER: Calling parent's onTemplateDeleted for ID: ${templateIdToDelete}`);
        onTemplateDeleted(templateIdToDelete); // Pass the ID of the deleted template
      } else {
        console.warn("TEMPLATE_MANAGER: onTemplateDeleted prop is not a function or wasn't passed.");
      }

        // Reset state and show success toast
        setShowDeleteDialog(false);
        setCurrentTemplate(null); // Clear the selected template for deletion
        toast({
            title: 'Template Deleted',
            description: `"${templateNameToDelete}" was successfully deleted.`
        });

        } catch (error) {
        console.error('Error deleting template:', error);
        toast({ title: 'Error', description: `Failed to delete template: ${error.message}`, variant: 'destructive' });
        } finally {
        setSubmitting(false);
        }
    };

  // Handle setting a template as active
  const handleSetActiveTemplate = async (templateId) => {
    try {
      setLoading(true);
      
      // First, deactivate all templates
      const { error: deactivateError } = await supabase
        .from('workout_templates')
        .update({ is_active: false })
        .eq('user_id', userId);
      
      if (deactivateError) throw deactivateError;
      
      // Then activate the selected template
      const { error: activateError } = await supabase
        .from('workout_templates')
        .update({ is_active: true })
        .eq('id', templateId)
        .eq('user_id', userId);
      
      if (activateError) throw activateError;
      
      // Update local state
      setTemplates(prev => 
        prev.map(t => ({
          ...t,
          is_active: t.id === templateId
        }))
      );
      
      // Update user workout settings
      const { error: settingsError } = await supabase
        .from('user_workout_settings')
        .upsert({
          id: userId,
          active_template: `custom_${templateId}`,
          updated_at: new Date().toISOString()
        });
      
      if (settingsError) throw settingsError;
      
      // Add this code to call the parent's template preference function
      const templateKey = `custom_${templateId}`;
      if (typeof onTemplateActivated === 'function') {
        console.log("TEMPLATE_MANAGER: Calling onTemplateActivated with:", templateKey);
        onTemplateActivated(templateKey);
      }
      
      toast({
        title: 'Template Activated',
        description: 'This workout template is now your active program'
      });
    } catch (error) {
      console.error('Error setting active template:', error);
      toast({
        title: 'Error',
        description: 'Failed to set active template',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a workout to a template
  const handleAddWorkout = () => {
    if (!selectedWorkout?.name?.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please provide a name for the workout',
        variant: 'destructive'
      });
      return;
    }
    
    setCurrentTemplate(prev => {
      // Generate a unique ID for the workout
      const workoutId = `workout_${Date.now()}`;
      
      // Create new workouts object with the new workout
      const updatedWorkouts = {
        ...prev.workouts,
        [workoutId]: {
          name: selectedWorkout.name,
          exercises: []
        }
      };
      
      return {
        ...prev,
        workouts: updatedWorkouts
      };
    });
    
    setShowWorkoutDialog(false);
    setSelectedWorkout(null);
  };

  // Handle removing a workout
  const handleRemoveWorkout = (workoutId) => {
    setCurrentTemplate(prev => {
      const { [workoutId]: removedWorkout, ...remainingWorkouts } = prev.workouts;
      return {
        ...prev,
        workouts: remainingWorkouts
      };
    });
  };

  // Handle adding an exercise to a workout
  const handleAddExercise = () => {
    // *** Use comboboxValue instead of selectedExerciseId ***
    if (!comboboxValue) {
      toast({ title: 'Exercise Required', description: 'Please select an exercise from the dropdown', variant: 'destructive' });
      return;
    }
    
    // Make sure this line is using selectedWorkoutId properly
    if (!currentTemplate || !selectedWorkoutId) {
      console.error("Missing context - Template:", currentTemplate, "Workout ID:", selectedWorkoutId);
      toast({ title: "Error", description: "Template or workout context lost.", variant: "destructive" });
      return;
    }

    // Find the selected exercise using comboboxValue from the *original* combined list
    // Combine custom and public lists to search within
    const allExercisesSource = [...customExercises, ...publicExercises];
    const exercise = allExercisesSource.find(e =>
      e.id.toString() === comboboxValue // Compare with comboboxValue state
    );

    if (!exercise) {
      toast({ title: 'Error', description: 'Selected exercise data not found', variant: 'destructive' });
      console.error("Could not find exercise with ID:", comboboxValue, "in custom/public lists");
      return;
    }

    // Add the exercise to the selected workout in the currentTemplate state
    setCurrentTemplate(prev => {
      if (!prev || !prev.workouts || !prev.workouts[selectedWorkoutId]) {
          console.error("State error: template or workout missing when adding exercise.");
          return prev; // Return previous state if structure is invalid
      }

      const currentWorkout = prev.workouts[selectedWorkoutId];
      // Create a unique instance ID for this exercise within this specific workout/template instance
      // Useful for list keys and potential reordering/editing later
      const newExerciseInstance = {
          instanceId: `${exercise.id}_${Date.now()}`,
          id: exercise.id, // Keep original ID for reference
          name: exercise.name,
          exercise_type: exercise.exercise_type || 'other', // Use default if missing
          // Use default values from the selected exercise, fallback to general defaults
          sets: exercise.default_sets ?? 3,
          reps: exercise.default_reps ?? 8,
          repType: exercise.default_rep_type ?? 'static',
          repMin: exercise.default_rep_min ?? 8,
          repMax: exercise.default_rep_max ?? 12,
          restTime: exercise.default_rest_time ?? 90,
          increment: exercise.default_increment ?? 5,
          notes: '', // Start with empty notes
          // Determine if it was originally a public exercise for potential display differences
          isPublic: publicExercises.some(pubEx => pubEx.id === exercise.id)
      };

      // Create updated exercises array
      const updatedExercises = [...(currentWorkout.exercises || []), newExerciseInstance];
      // Create updated workout object
      const updatedWorkout = { ...currentWorkout, exercises: updatedExercises };
      // Return updated template object
      return { ...prev, workouts: { ...prev.workouts, [selectedWorkoutId]: updatedWorkout } };
    });

    // Reset UI state (Dialog closing handles combobox state reset via onOpenChange)
    setShowAddExerciseDialog(false);
  };

  // Handle removing an exercise from a workout
  const handleRemoveExercise = (workoutId, exerciseIndex) => {
    setCurrentTemplate(prev => {
      const currentWorkout = prev.workouts[workoutId];
      const updatedExercises = [...currentWorkout.exercises];
      updatedExercises.splice(exerciseIndex, 1);
      
      const updatedWorkout = {
        ...currentWorkout,
        exercises: updatedExercises
      };
      
      return {
        ...prev,
        workouts: {
          ...prev.workouts,
          [workoutId]: updatedWorkout
        }
      };
    });
  };

  // Handle moving an exercise up or down in the list
  const handleMoveExercise = (workoutId, exerciseIndex, direction) => {
    setCurrentTemplate(prev => {
      const currentWorkout = prev.workouts[workoutId];
      const updatedExercises = [...currentWorkout.exercises];
      
      if (direction === 'up' && exerciseIndex > 0) {
        // Swap with the exercise above
        [updatedExercises[exerciseIndex], updatedExercises[exerciseIndex - 1]] = 
        [updatedExercises[exerciseIndex - 1], updatedExercises[exerciseIndex]];
      } else if (direction === 'down' && exerciseIndex < updatedExercises.length - 1) {
        // Swap with the exercise below
        [updatedExercises[exerciseIndex], updatedExercises[exerciseIndex + 1]] = 
        [updatedExercises[exerciseIndex + 1], updatedExercises[exerciseIndex]];
      }
      
      const updatedWorkout = {
        ...currentWorkout,
        exercises: updatedExercises
      };
      
      return {
        ...prev,
        workouts: {
          ...prev.workouts,
          [workoutId]: updatedWorkout
        }
      };
    });
  };

  // Render template list
  const renderTemplatesList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      );
    }
    
    if (templates.length === 0) {
      return (
        <div className="text-center py-12">
          <Dumbbell className="h-12 w-12 mx-auto text-muted-foreground opacity-30 mb-3" />
          <h3 className="text-lg font-semibold mb-2">No Custom Programs Yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first workout program to get started
          </p>
          <Button onClick={() => {
            setCurrentTemplate({...emptyTemplate});
            setShowCreateDialog(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workout Program
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {templates.map(template => (
          <Card key={template.id} className={template.is_active ? 'border-primary' : ''}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {template.name}
                    {template.is_active && (
                      <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {template.description || `${template.goal.charAt(0).toUpperCase() + template.goal.slice(1)} • ${template.level.charAt(0).toUpperCase() + template.level.slice(1)} • ${template.frequency}`}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentTemplate({...template});
                      setShowEditDialog(true);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  {userSettings?.activeTemplate !== `custom_${template.id}` && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetActiveTemplate(template.id)}
                      disabled={loading}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Activate
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      setCurrentTemplate(template);
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Render workouts in the template */}
                {template.workouts && Object.keys(template.workouts).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(template.workouts).map(([workoutId, workout]) => (
                      <div key={workoutId} className="border rounded-md p-3">
                        <div className="font-medium mb-2">{workout.name}</div>
                        {workout.exercises && workout.exercises.length > 0 ? (
                          <ul className="space-y-1 text-sm">
                            {workout.exercises.map(exercise => (
                              <li key={exercise.id || exercise.name} className="flex justify-between">
                                <span>{exercise.name}</span>
                                <span className="text-muted-foreground">
                                  {exercise.repType === 'static' 
                                    ? `${exercise.sets}×${exercise.reps}`
                                    : `${exercise.sets}×${exercise.repMin}-${exercise.repMax}`
                                  }
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">No exercises added</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-2 text-muted-foreground">No workouts added to this template</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderTemplateForm = () => {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="template-name">Program Name*</Label>
          <Input
            id="template-name"
            value={currentTemplate?.name || ''}
            onChange={(e) => setCurrentTemplate(prev => ({...prev, name: e.target.value}))}
            placeholder="e.g., My 5x5 Program"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="template-description">Description (Optional)</Label>
          <Textarea
            id="template-description"
            value={currentTemplate?.description || ''}
            onChange={(e) => setCurrentTemplate(prev => ({...prev, description: e.target.value}))}
            placeholder="A brief description of the program..."
            rows={3}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="template-goal">Primary Goal</Label>
            <Select
              value={currentTemplate?.goal || 'strength'}
              onValueChange={(value) => setCurrentTemplate(prev => ({...prev, goal: value}))}
            >
              <SelectTrigger id="template-goal">
                <SelectValue placeholder="Select goal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="strength">Strength</SelectItem>
                <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                <SelectItem value="endurance">Endurance</SelectItem>
                <SelectItem value="power">Power</SelectItem>
                <SelectItem value="general">General Fitness</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-level">Experience Level</Label>
            <Select
              value={currentTemplate?.level || 'beginner'}
              onValueChange={(value) => setCurrentTemplate(prev => ({...prev, level: value}))}
            >
              <SelectTrigger id="template-level">
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-frequency">Frequency</Label>
            <Select
              value={currentTemplate?.frequency || '3x per week'}
              onValueChange={(value) => setCurrentTemplate(prev => ({...prev, frequency: value}))}
            >
              <SelectTrigger id="template-frequency">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2x per week">2x per week</SelectItem>
                <SelectItem value="3x per week">3x per week</SelectItem>
                <SelectItem value="4x per week">4x per week</SelectItem>
                <SelectItem value="5x per week">5x per week</SelectItem>
                <SelectItem value="6x per week">6x per week</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Workouts Section */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Workouts</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                console.log("Add Workout button clicked!");
                setSelectedWorkout({...emptyWorkout});
                setShowWorkoutDialog(true); 
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Workout
            </Button>
          </div>

          {currentTemplate?.workouts && Object.keys(currentTemplate.workouts).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(currentTemplate.workouts).map(([workoutId, workout]) => (
                <Card key={workoutId} className="border border-muted">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-base">{workout.name}</CardTitle>
                      <div className="flex gap-1">

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                            setSelectedWorkoutId(workoutId);  // Use setSelectedWorkoutId instead
                            setShowAddExerciseDialog(true);
                        }}
                        >
                        <Plus className="h-4 w-4" />
                        <span className="sr-only">Add Exercise</span>
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveWorkout(workoutId)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Remove Workout</span>
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {workout.exercises && workout.exercises.length > 0 ? (
                      <div className="space-y-2">
                        {workout.exercises.map((exercise, index) => (
                          <div 
                            key={`${workoutId}_exercise_${index}`}
                            className="flex justify-between items-center p-2 border rounded-md"
                          >
                            <div>
                              <div className="font-medium">{exercise.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {exercise.repType === 'static'
                                  ? `${exercise.sets}×${exercise.reps} • ${exercise.restTime}s rest`
                                  : `${exercise.sets}×${exercise.repMin}-${exercise.repMax} • ${exercise.restTime}s rest`
                                }
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleMoveExercise(workoutId, index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUp className="h-4 w-4" />
                                <span className="sr-only">Move Up</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleMoveExercise(workoutId, index, 'down')}
                                disabled={index === workout.exercises.length - 1}
                              >
                                <ArrowDown className="h-4 w-4" />
                                <span className="sr-only">Move Down</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveExercise(workoutId, index)}
                              >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Remove Exercise</span>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-muted-foreground py-2">
                        No exercises added yet
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="border rounded-md p-6 text-center">
              <p className="text-muted-foreground">
                No workouts added yet. Add a workout to get started.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Return the main component with all dialogs
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Your Workout Programs</CardTitle>
            <CardDescription>Create and manage your custom workout programs</CardDescription>
          </div>
          <Button onClick={() => {
            setCurrentTemplate({...emptyTemplate});
            setShowCreateDialog(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Program
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {renderTemplatesList()}
      </CardContent>

      {/* Create Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(isOpen) => {
        setShowCreateDialog(isOpen);
        if (!isOpen) setCurrentTemplate(null);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Workout Program</DialogTitle>
            <DialogDescription>
              Design your own workout program with custom exercises and schedules
            </DialogDescription>
          </DialogHeader>
          {currentTemplate && renderTemplateForm()}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              disabled={submitting || !currentTemplate?.name?.trim()}
            >
              {submitting ? 'Creating...' : 'Create Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(isOpen) => {
        setShowEditDialog(isOpen);
        if (!isOpen) setCurrentTemplate(null);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Workout Program</DialogTitle>
            <DialogDescription>
              Update your workout program details and exercises
            </DialogDescription>
          </DialogHeader>
          {currentTemplate && renderTemplateForm()}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTemplate}
              disabled={submitting || !currentTemplate?.name?.trim()}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(isOpen) => {
        setShowDeleteDialog(isOpen);
        if (!isOpen) setCurrentTemplate(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Workout Program</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{currentTemplate?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTemplate}
              disabled={submitting}
            >
              {submitting ? 'Deleting...' : 'Delete Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        {/* Add Workout Dialog */}
        <Dialog open={showWorkoutDialog} onOpenChange={(isOpen) => {
        setShowWorkoutDialog(isOpen);
        if (!isOpen) setSelectedWorkout(null);
        }}>
        <DialogContent>
            <DialogHeader>
            <DialogTitle>Add Workout</DialogTitle>
            <DialogDescription>
                Add a new workout to your program
            </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
            <div className="space-y-2">
                <Label htmlFor="workout-name">Workout Name*</Label>
                <Input
                id="workout-name"
                value={selectedWorkout?.name || ''}
                onChange={(e) => setSelectedWorkout(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Push Day, Upper Body, etc."
                />
            </div>
            </div>
            <DialogFooter>
            <Button 
                variant="outline" 
                onClick={() => setShowWorkoutDialog(false)}
            >
                Cancel
            </Button>
            <Button
                onClick={handleAddWorkout}
                disabled={!selectedWorkout?.name?.trim()}
            >
                Add Workout
            </Button>
            </DialogFooter>
        </DialogContent>
        </Dialog>

        {/* Add Exercise Dialog */}
        <Dialog open={showAddExerciseDialog} onOpenChange={(isOpen) => {
          setShowAddExerciseDialog(isOpen);
          // Reset combobox state when dialog closes
          if (!isOpen) {
            setComboboxOpen(false);
            setComboboxValue("");
            setComboboxSearch("");
            // NO call to setSelectedExerciseId here
          }
        }}>
        {/* Use w-full or specific width, ensure max-h doesn't interfere strangely */}
        <DialogContent className="sm:max-w-[500px] p-0"> {/* Remove default padding */}
          <DialogHeader className="p-6 pb-4"> {/* Add padding back here */}
            <DialogTitle>Add Exercise to Workout</DialogTitle>
            <DialogDescription>
              Search and select an exercise to add.
            </DialogDescription>
          </DialogHeader>
          {/* --- START: Combobox Implementation --- */}
          <div className="px-6 pb-6 space-y-4"> {/* Add padding for content */}
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between"
                >
                  {comboboxValue
                    ? allAvailableExercises.find((ex) => ex.value === comboboxValue)?.label
                    : "Select exercise..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}> {/* We handle filtering manually */}
                  <CommandInput
                    placeholder="Search exercise..."
                    value={comboboxSearch}
                    onValueChange={setComboboxSearch}
                  />
                  <CommandList> {/* Wrap CommandGroup and CommandEmpty in CommandList for scrolling */}
                      <CommandEmpty>No exercise found.</CommandEmpty>
                      <CommandGroup>
                        {allAvailableExercises
                          .filter(ex => ex.label.toLowerCase().includes(comboboxSearch.toLowerCase())) // Manual filter
                          .map((exercise) => (
                          <CommandItem
                            key={exercise.value}
                            value={exercise.value}
                            onSelect={(currentValue) => {
                              // currentValue is the ID (exercise.value)
                              setComboboxValue(currentValue === comboboxValue ? "" : currentValue);
                              setComboboxOpen(false);
                              setComboboxSearch(""); // Clear search on select
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                comboboxValue === exercise.value ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="flex-1">{exercise.label}</span>
                            {exercise.isPublic && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                Public
                                </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {/* --- END: Combobox Implementation --- */}

          {/* Keep the DialogFooter, but disable button based on comboboxValue */}
          <DialogFooter className="p-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAddExerciseDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddExercise}
              disabled={!comboboxValue} // Correctly uses comboboxValue
            >
              Add Exercise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Card>
  );
};

export default WorkoutTemplateManager;